import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import MarkdownCard from "@/components/app/MarkdownCard";
import { MapPin, Phone, Store as StoreIcon, Bell, QrCode, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function StorePage() {
  const { slug } = useParams();
  const [store, setStore] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [followEmail, setFollowEmail] = useState("");
  const [followBusy, setFollowBusy] = useState(false);

  const follow = async () => {
    if (!followEmail) return;
    setFollowBusy(true);
    try {
      const { data } = await api.post(`/stores/${slug}/follow`, { email: followEmail });
      toast.success(data.already ? "You're already following — we'll email you on new deals" : "You're in! We'll email you when new markdowns are posted");
      setFollowEmail("");
    } catch (e) { toast.error("Could not follow"); }
    finally { setFollowBusy(false); }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, m] = await Promise.all([
          api.get(`/stores/${slug}`),
          api.get(`/markdowns`, { params: { store_slug: slug } }),
        ]);
        setStore(s.data);
        setItems(m.data);
      } catch (e) {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-16 text-slate-500">Loading…</div>;
  if (notFound || !store) return <div className="max-w-7xl mx-auto px-4 py-16 text-center">
    <h2 className="text-2xl font-display font-bold">Store not found</h2>
    <Link to="/stores" className="text-emerald-800 underline underline-offset-4">Back to all stores</Link>
  </div>;

  return (
    <div>
      <section className="hero-mesh border-b border-slate-200/70">
        {store.shopfront_path && (
          <div className="w-full h-40 sm:h-56 overflow-hidden">
            <img src={imageUrl(store.shopfront_path)} alt={store.store_name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          {store.logo_path ? (
            <img src={imageUrl(store.logo_path)} alt={store.store_name} className="w-20 h-20 rounded-2xl object-cover bg-white border-4 border-white shadow-lg" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-emerald-800 text-white grid place-items-center font-display font-black text-3xl shadow-lg">
              {(store.store_name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-3xl sm:text-4xl font-display font-extrabold tracking-tight text-slate-900" data-testid="store-name">{store.store_name}</h1>
            {store.founding_seller && <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider bg-fuchsia-100 text-fuchsia-800 rounded-full px-2.5 py-0.5 mt-1" data-testid="store-founding-badge"><Award className="w-3.5 h-3.5" />Founding Seller</span>}
            {store.tagline && <p className="text-emerald-800 italic mt-1">{store.tagline}</p>}
            <div className="mt-1 flex flex-wrap gap-3 text-sm text-slate-600">
              {store.region && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{store.region}</span>}
              {store.phone && <span className="inline-flex items-center gap-1"><Phone className="w-4 h-4" />{store.phone}</span>}
              {store.address && <span className="inline-flex items-center gap-1"><StoreIcon className="w-4 h-4" />{store.address}</span>}
            </div>
            <div className="mt-2 text-sm text-emerald-900 font-semibold">{items.length} live markdowns</div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="rounded-2xl border border-fuchsia-200 bg-fuchsia-50/50 p-4 flex flex-col sm:flex-row gap-4 items-center" data-testid="store-qr-panel">
          <img
            alt={`Scan to view ${store.store_name} on Blurblive`}
            src={`https://api.qrserver.com/v1/create-qr-code/?size=160x160&margin=2&data=${encodeURIComponent(window.location.origin + "/store/" + slug)}`}
            className="w-24 h-24 rounded-lg bg-white border-2 border-fuchsia-200 p-1 shrink-0"
          />
          <div className="flex-1 text-center sm:text-left">
            <div className="font-display font-bold text-fuchsia-900 text-base flex items-center justify-center sm:justify-start gap-1.5"><QrCode className="w-4 h-4" />Scan to visit this store's page</div>
            <div className="text-slate-600 text-sm mt-0.5">Shoppers can share this QR or stick it on the window — one tap lands them on every current markdown from {store.store_name}.</div>
            <div className="font-mono text-xs text-slate-500 mt-1 break-all">{window.location.host}/store/{slug}</div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 flex flex-col sm:flex-row gap-3 items-center" data-testid="follow-panel">
          <div className="flex items-center gap-2 text-emerald-900"><Bell className="w-5 h-5" /><span className="font-display font-bold">Follow {store.store_name}</span></div>
          <span className="text-xs text-slate-600 flex-1">Get an email the moment a new markdown lands.</span>
          <Input placeholder="you@email.com" value={followEmail} onChange={(e)=>setFollowEmail(e.target.value)} className="sm:w-64" data-testid="follow-email" />
          <Button onClick={follow} disabled={followBusy || !followEmail} className="bg-emerald-800 hover:bg-emerald-900" data-testid="follow-btn">{followBusy ? "Adding…" : "Follow"}</Button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {items.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-slate-300 rounded-2xl bg-white text-slate-500" data-testid="store-empty">
            No live markdowns from this store right now. Check back soon.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {items.map(m => <MarkdownCard key={m.id} m={m} />)}
          </div>
        )}
      </section>
    </div>
  );
}
